// Members tab — list of users assigned to a role, with add/remove.

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApi, ApiRequestError } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { rolesApi, usersApi } from '@/lib/api/usersRoles'
import type { RoleMember } from '@/lib/api/usersRoles'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { useToast } from '@/lib/hooks/use-toast'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { Badge } from '@/components/ui/badge'
import { UserPlus, X, Search, Loader2 } from 'lucide-react'

interface MembersTabProps {
  roleId: string
  roleName: string
}

export function MembersTab({ roleId, roleName }: MembersTabProps) {
  const { slug } = useTenant()
  const api = useApi()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const rolesClient = rolesApi(api)

  const [addOpen, setAddOpen] = useState(false)
  const [removeTarget, setRemoveTarget] = useState<RoleMember | null>(null)

  const { data: members, error, isLoading, refetch } = useQuery({
    queryKey: queryKeys.roles.members(slug, roleId),
    queryFn: () => rolesClient.members(roleId),
  })

  const removeMutation = useMutation({
    mutationFn: (userId: string) => rolesClient.removeMember(roleId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.roles.members(slug, roleId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.roles.all(slug) })
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all(slug) })
      toast({ title: 'Member removed' })
      setRemoveTarget(null)
    },
    onError: (err) => {
      toast({
        title: 'Failed to remove member',
        description: err instanceof ApiRequestError ? err.message : 'An unexpected error occurred.',
        variant: 'destructive',
      })
    },
  })

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-12 animate-pulse rounded-md bg-muted" />
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <ErrorState
        message="Failed to load members"
        technicalDetail={error.message}
        onRetry={() => refetch()}
      />
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {members?.length === 0
            ? 'No members assigned to this role.'
            : `${members?.length} member${members?.length !== 1 ? 's' : ''}`}
        </p>
        <Button size="sm" onClick={() => setAddOpen(true)}>
          <UserPlus className="mr-2 h-4 w-4" />
          Add Member
        </Button>
      </div>

      {members && members.length > 0 && (
        <div className="overflow-hidden rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium">Name / Email</th>
                <th className="px-4 py-3 text-left font-medium">Assigned</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.user_id} className="border-b last:border-0">
                  <td className="px-4 py-3">
                    <div>
                      <div className="flex items-center gap-2 font-medium">
                        {member.display_name || member.email}
                        {member.user_type === 'api' && (
                          <Badge variant="outline" className="text-xs">API</Badge>
                        )}
                      </div>
                      {member.display_name && (
                        <div className="text-xs text-muted-foreground">{member.email}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {new Date(member.assigned_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setRemoveTarget(member)}
                      title="Remove member"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add member dialog */}
      <AddMemberDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        roleId={roleId}
        roleName={roleName}
        existingMemberIds={members?.map((m) => m.user_id) ?? []}
      />

      {/* Remove member confirmation */}
      {removeTarget && (
        <AlertDialog open={!!removeTarget} onOpenChange={(open) => !open && setRemoveTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove member</AlertDialogTitle>
              <AlertDialogDescription>
                Remove <strong>{removeTarget.display_name || removeTarget.email}</strong> from
                role <strong>{roleName}</strong>? They will lose any permissions granted through
                this role.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={removeMutation.isPending}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => removeMutation.mutate(removeTarget.user_id)}
                disabled={removeMutation.isPending}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {removeMutation.isPending ? 'Removing...' : 'Remove'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  )
}

// --- Add Member Dialog ---
// Shows a searchable list of tenant users, filtering out existing members.

interface AddMemberDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  roleId: string
  roleName: string
  existingMemberIds: string[]
}

function AddMemberDialog({
  open,
  onOpenChange,
  roleId,
  roleName,
  existingMemberIds,
}: AddMemberDialogProps) {
  const { slug } = useTenant()
  const api = useApi()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const rolesClient = rolesApi(api)
  const usersClient = usersApi(api)

  const [searchInput, setSearchInput] = useState('')
  const debouncedSearch = useDebounce(searchInput, 300)

  const { data: allUsers, isLoading: loadingUsers } = useQuery({
    queryKey: queryKeys.users.list(slug, { search: debouncedSearch || undefined, include_api: true }),
    queryFn: () => usersClient.list({ search: debouncedSearch || undefined, include_api: true }),
    enabled: open,
  })

  const addMutation = useMutation({
    mutationFn: (userId: string) => rolesClient.addMember(roleId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.roles.members(slug, roleId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.roles.all(slug) })
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all(slug) })
      toast({ title: 'Member added' })
    },
    onError: (err) => {
      toast({
        title: 'Failed to add member',
        description: err instanceof ApiRequestError ? err.message : 'An unexpected error occurred.',
        variant: 'destructive',
      })
    },
  })

  // Filter out users already in the role
  const existingSet = new Set(existingMemberIds)
  const availableUsers = allUsers?.filter((u) => !existingSet.has(u.id)) ?? []

  function handleClose(isOpen: boolean) {
    if (!isOpen) setSearchInput('')
    onOpenChange(isOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[80vh] sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add member to {roleName}</DialogTitle>
          <DialogDescription>
            Search for a user to add to this role.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name or email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9"
            autoFocus
          />
        </div>

        <div className="max-h-64 overflow-y-auto">
          {loadingUsers ? (
            <div className="space-y-2 py-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-10 animate-pulse rounded-md bg-muted" />
              ))}
            </div>
          ) : availableUsers.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              {debouncedSearch ? 'No matching users found.' : 'All users are already members.'}
            </div>
          ) : (
            <div className="space-y-1 py-1">
              {availableUsers.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between rounded-md px-3 py-2 hover:bg-muted/50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 truncate text-sm font-medium">
                      {user.display_name || user.email}
                      {user.user_type === 'api' && (
                        <Badge variant="outline" className="text-xs">API</Badge>
                      )}
                    </div>
                    {user.display_name && (
                      <div className="truncate text-xs text-muted-foreground">{user.email}</div>
                    )}
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => addMutation.mutate(user.id)}
                    disabled={addMutation.isPending}
                  >
                    {addMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      'Add'
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
